import { useMemo } from 'react'

import { ProductColorWithUnavailable } from '../../../types/figs'
import { ProductColor, Variant } from '../../../types/graphql'

export function useColorsWithUnavailable({
  colors,
  colorKey,
  variants,
  sizeName,
}: {
  colors: ReadonlyArray<ProductColor>
  colorKey: string | undefined
  variants: ReadonlyArray<Variant>
  sizeName?: string | null
}) {
  return useMemo(() => {
    const sizedVariants: ReadonlyArray<Variant> = variants.filter(o => o.size === sizeName)

    const colorsWithStock = new Set(variants.filter(o => !o.soldOut).map(o => o.rawColor))

    const sizedVariantsColorsSoldOut: Record<string, boolean> = sizedVariants.reduce(
      (accum, curr) => ({
        ...accum,
        [curr.color]: curr.soldOut,
      }),
      {}
    )

    const isCurrentColorCompletelySoldOut: boolean = Boolean(
      colorKey && !colorsWithStock.has(colorKey)
    )

    const getColorsWithUnavailable = (): ProductColorWithUnavailable[] => {
      if (variants.length === 0) {
        return colors.map(o => ({ ...o, unavailable: false }))
      }

      if (sizeName) {
        /**
         * If the user HAS selected a size we want each color to show as sold-out
         * if the variant of that color and selected size is sold-out.
         */
        return colors.map(o => ({
          ...o,
          unavailable: !!o.rawName && !!sizedVariantsColorsSoldOut[o.rawName],
        }))
      }

      return colors.map(o => ({
        ...o,
        unavailable: !colorsWithStock.has(o.rawName),
      }))
    }

    return {
      colorsWithUnavailable: getColorsWithUnavailable(),
      isCurrentColorCompletelySoldOut,
    }
  }, [colorKey, colors, sizeName, variants])
}
